import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MeetingInformation from 'Components/UI/MeetingInformation/MeetingInformationTooltip';
import RichTextEditor from 'Components/UI/RichTextEditor';
import RichTextEditorCustomContextMenu from 'Components/UI/RichTextEditorCustomContextMenu';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import useResourceDeprecated from 'Hooks/useResourceDeprecated';
import useDebounce from 'Hooks/useDebounce';
import relationships from 'Consts/Relationships';
import { pickComponentAndProps } from 'Helpers/Affordances';
import { render } from 'Helpers/template';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import MeetingTemplateDialog from 'Components/UI/TemplateDialog';
import useUserContext from 'Context/UserContext';
import { openModal } from 'Helpers/ModalHelper';
import modalConsts from 'Consts/ModalNames';
import fr from 'facet-rest';
import TemplateOptionsMenu from 'Components/UI/TemplateOptionsMenu';
import { TemplatesProvider } from 'Context/TemplateContext';
import { TEMPLATE_MENU_ICON_OPTIONS, TEMPLATE_TYPES } from 'Components/UI/TemplateOptionsMenu/utils';
import {
  FDPaper, FDPrimaryActionButton, FDSecondaryActionIconButton, FDStack,
} from '@facet-wealth/design';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useTheme from '@mui/material/styles/useTheme';
import { useSnackbarContext } from 'Context/SnackbarContext';

const useStyles = buildUseStyles('NotesTab', theme => ({
  root: {
    height: '100%',
  },
  templateSelectContainer: {
    paddingBottom: 0,
    width: '60%',
    paddingRight: theme.spacing(1),
    alignSelf: 'center',
    '& div.MuiSelect-select': {
      padding: theme.spacing(1),
    },
    '& label': {
      transform: 'translate(14px, 10px) scale(1)',
    },
  },
  templateHeader: {
    display: 'flex',
    width: '60%',
    justifyContent: 'flex-end',
  },
}));

function Notes({
  className,
  householdResource,
  selectedTemplate,
  meetingResource,
  updateSelectedTemplate,
  saveSelectedTemplate,
  isValidTemplateName,
  getNoteCollection,
  noteResource,
}) {
  const classes = useStyles();
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const { resource, setResource } = useResourceDeprecated(noteResource);
  const [saved, setSaved] = useState('saved');
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [showChangeTemplateDialog, setShowChangeTemplateDialog] = useState(false);
  const [showDeleteTemplateDialog, setShowDeleteTemplateDialog] = useState(false);
  const [selectedTemplateId] = useState();
  const [content, setContent] = useState(resource?.attr('content'));
  const { agentResource } = useUserContext();
  const theme = useTheme();
  const {
    rootNodeProps,
    logResourceEvent,
    logResourceEventSuccess,
    logResourceEventFailureWithError,
  } = useEventTrackingLogEvent({
    nodeName: 'Notes',
  });

  const handleSaveNote = async (resourceToSave, isClickToSave) => {
    const eventName = 'Clicked Save Note';
    try {
      if (isClickToSave) {
        logResourceEvent(eventName, resourceToSave);
      }
      setSaved('saving'); // Indicate that we are in the saving process
      await resourceToSave.submit();
      setSaved((prevState) => {
        // If the user has not updated the state at all before this runs then set it to saved.
        if (prevState === 'saving') {
          return 'saved';
        }

        // Otherwise make a pass through, as they have continued typing
        return prevState;
      }); // Indicate that we are in the saving process
      if (isClickToSave) {
        logResourceEventSuccess(eventName, resourceToSave);
      }
    } catch (err) {
      if (isClickToSave) {
        logResourceEventFailureWithError(eventName, resourceToSave, err);
      }
      setSaved('error'); // Indicate that we are not saved since the save wasn't successful

      switch (err.status) {
        case 409:
          // TODO: Remove once better error messages are implemented
          displayErrorMessage(
            err,
            'Something has gone wrong and the server is trying to create a new note unsuccessfully. Please copy/paste your note elsewhere (for safe keeping) and contact #appsupport.',
          );
          break;
        default:
          displayErrorMessage(err, err.errors);
      }
    }
  };

  const applyAutoSave = useDebounce((value) => {
    handleSaveNote(resource.copy().attr('content', value), false);
  });

  const updateNote = (v) => {
    setResource(resource.attr('content', v));
    applyAutoSave(v);

    setSaved('unsaved');
  };

  const handleInsertTemplate = (t) => {
    setContent(render(resource.attr('content') + t.attr('content')));
  };

  const { component: NoteComponent, props: noteProps } = pickComponentAndProps(agentResource, {
    [relationships.notesContextMenu]: [RichTextEditorCustomContextMenu, {
      onChange: updateNote,
      value: content,
      test: 'note-input',
    }],
    [relationships.default]: [RichTextEditor, {
      onChange: updateNote,
      value: content,
    }],
  });

  const handleDeleteNoteTemplate = async () => {
    if (selectedTemplate) {
      try {
        await selectedTemplate.delete();
        getNoteCollection();
        displaySuccess('Template was successfully deleted.');
      } catch (e) {
        displayErrorMessage(e, e.errors);
      }
    }
  };

  useEffect(() => {
    if (selectedTemplateId && selectedTemplate) {
      setContent(render(selectedTemplate.attr('content')));
    }
  }, [selectedTemplate, selectedTemplateId]);

  let saveButtonProps = {
    color: 'secondary',
  };

  switch (saved) {
    case 'saving':
      saveButtonProps = {
        ...saveButtonProps,
        saving: true,
        text: 'Saving...',
      };
      break;
    case 'unsaved':
      saveButtonProps = {
        ...saveButtonProps,
        text: 'Save Note',
      };
      break;
    case 'error':
      saveButtonProps = {
        ...saveButtonProps,
        color: 'red',
        text: 'Error Saving Note',
      };
      break;
    case 'saved':
      saveButtonProps = {
        ...saveButtonProps,
        disabled: true,
        text: 'Note Saved',
      };
      break;
    default:
      throw new Error('invalid saved state');
  }

  return (
    <>
      <div
        className={classNames(classes.root, className)}
        style={{ backgroundColor: theme.palette.background.subtle }}
        test="mission-control-note-root"
        {...rootNodeProps}
      >
        <FDStack direction={'column'} spacing={1} padding={1} height={'100%'}>
          <MeetingInformation
            householdResource={householdResource}
            meetingResource={meetingResource}
          />
          <FDPaper appearance={'elevated'} padding={1} height={'100%'}>
            <FDStack direction={'column'} spacing={2} alignItems={'center'} height={'100%'}>
              <NoteComponent {...noteProps} size={'full'} />
              <FDStack spacing={2} justifyContent={'space-between'} width={'100%'} alignItems={'center'}>
                <FDStack spacing={1} alignItems={'center'}>
                  {noteResource.id() !== '0' && (
                  <FDSecondaryActionIconButton
                    icon={'accessTime'}
                    label={'History'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openModal(modalConsts.historyFlyout, [
                        { title: btoa('Note History') },
                        { link: btoa(noteResource.relationship('history').link('related').url()) },
                      ]);
                    }}
                  />
                  )}
                  <TemplatesProvider
                    templatesLinkUrl={meetingResource?.relationship(fr.relationships.noteTemplates.name)?.link('related').url()}
                    pinnedTemplatesLinkUrl={agentResource?.relationship(fr.relationships.pinnedNoteTemplates.name)?.link('related').url()}
                    initialState={{}}
                  >
                    <TemplateOptionsMenu
                      onInsertTemplate={handleInsertTemplate}
                      templateDraftContent={resource.attr('content')}
                      templateType={TEMPLATE_TYPES.note}
                      menuIcon={TEMPLATE_MENU_ICON_OPTIONS.description}
                      menuIconSize={'sm'}
                    />
                  </TemplatesProvider>
                </FDStack>
                <FDPrimaryActionButton
                  loading={saveButtonProps.saving}
                  disabled={!resource.isValid() || saveButtonProps.disabled}
                  onClick={() => handleSaveNote(resource.copy(), true)}
                  label={saveButtonProps.text}
                  size={'sm'}
                />
              </FDStack>
            </FDStack>
          </FDPaper>
        </FDStack>
      </div>
      {showChangeTemplateDialog && (
      <ConfirmationDialog
        onCancel={() => setShowChangeTemplateDialog(false)}
        title="Are you sure you want to change note templates?"
        message="The current text will be replaced."
        onConfirm={() => {
          setShowChangeTemplateDialog(false);
          updateSelectedTemplate(selectedTemplateId);
        }}
        showCloseIcon={false}
      />
      )}
      {showDeleteTemplateDialog && (
      <ConfirmationDialog
        onCancel={() => setShowDeleteTemplateDialog(false)}
        title="Are you sure you want to delete this note template?"
        message="The current text will not be replaced."
        onConfirm={() => {
          setShowDeleteTemplateDialog(false);
          handleDeleteNoteTemplate();
        }}
        showCloseIcon={false}
      />
      )}
      {showSaveTemplateDialog && (
      <MeetingTemplateDialog
        template={selectedTemplate}
        onSave={() => {
          saveSelectedTemplate(resource.attr('content'));
          setShowSaveTemplateDialog(false);
        }}
        onCancel={() => {
          setShowSaveTemplateDialog(false);
        }}
        saveLabel="Save template"
        isValidTemplateName={isValidTemplateName}
      />
      )}
    </>
  );
}

Notes.defaultProps = {
  templateCollection: null,
  selectedTemplate: null,
  showMeetingInformation: false,
};

Notes.propTypes = {
  className: PropTypes.string,
  householdResource: PropTypes.object.isRequired,
  noteCollection: PropTypes.object.isRequired,
  templateCollection: PropTypes.object,
  selectedTemplate: PropTypes.object,
  meetingResource: PropTypes.object.isRequired,
  updateSelectedTemplate: PropTypes.func.isRequired,
  saveSelectedTemplate: PropTypes.func.isRequired,
  isValidTemplateName: PropTypes.func.isRequired,
  getNoteCollection: PropTypes.func.isRequired,
  showMeetingInformation: PropTypes.bool,
};

export default Notes;
